import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { endOfMonth, setMonth, setYear, startOfMonth } from 'date-fns';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';

import { getMonthsOfCurrentYear } from 'src/utils/functions';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import CustomDatePicker from 'src/components/custom-datepicker/custom-datepicker';

import {
  ChequeCustomerType,
  IChequeReceived,
  IChequeTableFilters,
  IChequeTableFilterValue,
} from 'src/types/cheque';

type Props = {
  filters: IChequeTableFilters;
  onFilters: (name: string, value: IChequeTableFilterValue) => void;
  handleResetFilters: VoidFunction;
  canReset: boolean;
  received: IChequeReceived[];
  tableStatus: string;
};

export default function ChequeTableToolbar({
  filters,
  onFilters,
  handleResetFilters,
  canReset,
  received,
  tableStatus,
}: Props) {
  const [input, setInput] = useState<string>('');

  const months = getMonthsOfCurrentYear();
  const [month, setMonthState] = useState<string | null>(null);

  const [year, setYearState] = useState<number | null>(null);

  const handleFilterMonth = useCallback(
    (event: SelectChangeEvent<string>) => {
      const selectedMonth = event.target.value;
      setMonthState(selectedMonth);

      if (year) {
        const monthIndex = months.indexOf(selectedMonth);
        const selectedDate = setMonth(setYear(new Date(), year), monthIndex);

        onFilters('startDate', startOfMonth(selectedDate));
        onFilters('endDate', endOfMonth(selectedDate));
      }
    },
    [months, year, onFilters]
  );

  const handleFilterYear = (event: SelectChangeEvent<string>) => {
    const selectedYear = Number(event.target.value);
    setYearState(selectedYear);

    if (month) {
      const monthIndex = months.indexOf(month);
      const selectedDate = setMonth(setYear(new Date(), selectedYear), monthIndex);

      onFilters('startDate', startOfMonth(selectedDate));
      onFilters('endDate', endOfMonth(selectedDate));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      onFilters('generalSearch', value);
    }, 500),
    []
  );

  const filterByReceived = useCallback((event: SelectChangeEvent<string>) => {
    onFilters('receivedVia', event.target.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterSaleType = useCallback((event: SelectChangeEvent<string>) => {
    onFilters('type', event.target.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setInput(value);

    debouncedSearch(value);
  };

  const handleFilterReceivedDate = useCallback(
    (date: any) => {
      onFilters('receivedDate', date);
    },
    [onFilters]
  );

  const handleFilterPaidDate = useCallback(
    (date: any) => {
      onFilters('paidDate', date);
    },
    [onFilters]
  );

  const clearSearchText = () => {
    setInput('');

    debouncedSearch('');
  };

  const clearAll = useCallback(() => {
    setInput('');
    handleResetFilters();

    setMonthState(null);
    setYearState(null);
  }, [handleResetFilters]);

  return (
    <Stack
      spacing={2}
      alignItems={{ xs: 'flex-end', md: 'center' }}
      direction={{
        xs: 'column',
        // md: 'row',
      }}
      sx={{
        p: 2.5,
        pr: { xs: 2.5, md: 1 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} flexGrow={1} sx={{ width: 1 }}>
        <Stack direction="row" alignItems="center" spacing={2} flexGrow={1} sx={{ width: 1 }}>
          <TextField
            fullWidth
            value={input}
            onChange={handleSearchChange}
            placeholder="Search by anything..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment:
                input.length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearchText}>
                      <Iconify icon="eva:close-fill" color="text.disabled" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Stack>

        <FormControl
          sx={{
            flexShrink: 0,
            width: { xs: 1, md: 180 },
          }}
        >
          <InputLabel>Year</InputLabel>

          <Select
            value={year ? year.toString() : ''}
            onChange={handleFilterYear}
            input={<OutlinedInput label="Year" />}
            renderValue={(selected) => (
              <Label variant="soft" color="primary">
                {selected}
              </Label>
            )}
            sx={{ textTransform: 'capitalize' }}
          >
            {[
              new Date().getFullYear() - 1,
              new Date().getFullYear(),
              new Date().getFullYear() + 1,
            ].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          sx={{
            flexShrink: 0,
            width: { xs: 1, md: 180 },
          }}
        >
          <InputLabel>Month</InputLabel>

          <Select
            value={month ?? ''}
            onChange={handleFilterMonth}
            input={<OutlinedInput label="Month" />}
            renderValue={(selected) => (
              <Label variant="soft" color="primary">
                {selected}
              </Label>
            )}
            sx={{ textTransform: 'capitalize' }}
          >
            {months.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} flexGrow={1} sx={{ width: 1 }}>
        <FormControl
          sx={{
            flexShrink: 0,
            width: { xs: 1, md: 180 },
          }}
        >
          <InputLabel>Received Via</InputLabel>

          <Select
            value={filters.receivedVia ?? ''}
            onChange={filterByReceived}
            input={<OutlinedInput label="Received Via" />}
            renderValue={(selected) => (
              <Label variant="soft" color="primary">
                {selected}
              </Label>
            )}
            sx={{ textTransform: 'capitalize' }}
          >
            {received.map((option) => (
              <MenuItem key={option.receivedVia} value={option.receivedVia}>
                {option.receivedVia}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {tableStatus !== 'all' ? (
          <CustomDatePicker
            disableFuture
            label="Received Date"
            value={filters.receivedDate}
            onChange={handleFilterReceivedDate}
          />
        ) : (
          <>
            <CustomDatePicker
              disableFuture
              label="Received Date"
              value={filters.receivedDate}
              onChange={handleFilterReceivedDate}
            />
            <CustomDatePicker
              disableFuture
              label="Paid Date"
              value={filters.paidDate}
              onChange={handleFilterPaidDate}
            />
          </>
        )}

        <FormControl
          sx={{
            flexShrink: 0,
            width: { xs: 1, md: 180 },
          }}
        >
          <InputLabel>Sale Type</InputLabel>

          <Select
            value={filters.type ?? ''}
            onChange={filterSaleType}
            input={<OutlinedInput label="Sale Type" />}
            renderValue={(selected) => (
              <Label variant="soft" color="primary">
                {selected}
              </Label>
            )}
            sx={{ textTransform: 'capitalize' }}
          >
            {Object.values(ChequeCustomerType).map((option) => (
              <MenuItem
                key={option}
                value={option}
                sx={{
                  textTransform: 'capitalize',
                }}
              >
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {canReset && (
          <Button
            color="error"
            onClick={clearAll}
            variant="text"
            sx={{ textDecoration: 'underline' }}
          >
            Clear
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
